/**
 * RunTimeline — everything that happened to one run, in order, with real times.
 *
 * Two views of the same run, because they answer different questions:
 *
 *  - The PHASE RAIL answers "where did it get to?" — resolved → generated → sent →
 *    delivered → downloaded. It is derived from evidence (source keys, artifacts, SES
 *    message ids, delivery events, access rows), not from optimism, so a run that says
 *    `sent` but has no Delivery event shows delivery as still pending.
 *  - The EVENT LIST answers "when, exactly?" — and only ever shows a timestamp the
 *    system actually recorded. Nothing here is interpolated: a phase with no stored
 *    time appears in the rail and not in the list, rather than being given a plausible
 *    one. A fabricated time in an audit trail is worse than a missing one.
 *
 * ONE TONE VOCABULARY. The rail's pills and the list's markers are `Badge`, not a private
 * set of tone maps applied through inline styles. Two systems for "state is never colour
 * alone" means a change to the badge treatment silently stops applying to the rail.
 *
 * REPEATED ROWS ARE SUMMARISED, FAILURES ARE NOT. A run with 100 opens used to render 100
 * identical entries — a second copy of two tables that sit on the same page in a 24px-row
 * form, and 7,000px of scrolling between the reader and the grants card. Sends, deliveries,
 * opens, clicks and successful downloads collapse into one entry that names the count, the
 * first and the last time, and the table below that carries the rows. Every bounce,
 * complaint, rejection, revocation, expired-link click and failure stays its own event:
 * those are the ones the ordering matters for.
 *
 * `stale` is rendered as its own outcome throughout, never folded into `failed`
 * (CONTRACT §8.3): nothing was sent, and that was the correct behaviour. A PARTIAL run is
 * the other half of that rule (CONTRACT-COVERAGE): something WAS sent, and it covers less
 * than its dates claim, so the finish event says so rather than showing a plain green tick
 * over a part day.
 *
 * Masking (CONTRACT §8.6): token hashes are shown last-4 only. Recipient addresses are
 * business mailboxes and are shown in full — that is the point of a delivery log. So is a
 * bounce diagnostic: it is never truncated here, because the reason an address failed sits
 * at the END of an SMTP string and this component is its only surface.
 */

import { useState } from 'react';
import type { ReactNode } from 'react';

import Badge, { type BadgeTone } from '@/components/Badge';
import Button from '@/components/Button';
import {
  coverageOf,
  formatCount,
  istDateTimeExact,
  maskToken,
  misalignedNote,
  pluralise,
  timeAgo,
  toDate,
} from '@/lib/format';
import { reportShapeInfo } from '@/lib/types';
import type { AccessRow, Grant, Run, SesEvent } from '@/lib/types';

export type TimelineTone = 'default' | 'ok' | 'warn' | 'danger' | 'info' | 'muted';

/** The timeline's tones, said in the badge's vocabulary. One system, not two. */
const BADGE_TONE: Record<TimelineTone, BadgeTone> = {
  default: 'neutral',
  ok: 'ok',
  warn: 'warn',
  danger: 'danger',
  info: 'info',
  muted: 'muted',
};

export interface TimelineEvent {
  id: string;
  /** Milliseconds since epoch — only events with a real recorded time are listed. */
  sortKey: number;
  at: Date;
  title: string;
  detail?: ReactNode;
  tone: TimelineTone;
  glyph: string;
}

export interface TimelineSources {
  grants?: Grant[];
  access?: AccessRow[];
  sesEvents?: SesEvent[];
  /**
   * How many grants the run HAS, from the API — not how many this page happens to hold.
   * A run that links every recording mints one grant per recording; stating the loaded
   * page size as fact wrote "200 links issued" into an audit trail that should have said
   * 575.
   */
  grantTotal?: number | null;
  /** True when the grant list is a page of a longer one. */
  grantsTruncated?: boolean;
}

const SES_SPEC: Record<string, { title: string; tone: TimelineTone; glyph: string }> = {
  Send: { title: 'Handed to SES', tone: 'info', glyph: '↑' },
  Delivery: { title: 'Delivered', tone: 'ok', glyph: '✓' },
  Bounce: { title: 'Bounced', tone: 'danger', glyph: '⊘' },
  Complaint: { title: 'Marked as spam', tone: 'danger', glyph: '⚑' },
  Reject: { title: 'Rejected by SES', tone: 'danger', glyph: '✕' },
  Open: { title: 'Email opened', tone: 'muted', glyph: '◌' },
  Click: { title: 'Link clicked in email', tone: 'info', glyph: '↗' },
  DeliveryDelay: { title: 'Delivery delayed', tone: 'warn', glyph: '◔' },
  RenderingFailure: { title: 'Template failed to render', tone: 'danger', glyph: '✕' },
};

/**
 * Event types that repeat without anything changing, so N of them say no more than one
 * plus a count. Everything NOT in this set is a state transition or a failure and is
 * always listed on its own line.
 */
const SES_COLLAPSIBLE: Record<string, string> = {
  Send: 'messages',
  Delivery: 'confirmations',
  Open: 'opens',
  Click: 'clicks',
};

/** `first 14 Sep 2026, 09:14:02 IST · last 16 Sep 2026, 18:40:11 IST`, or one time. */
function spanLabel(times: Date[]): string {
  const first = times[0];
  const last = times[times.length - 1];
  if (!first) return '';
  if (!last || first.getTime() === last.getTime()) return istDateTimeExact(first);
  return `first ${istDateTimeExact(first)} · last ${istDateTimeExact(last)}`;
}

/** Merge the run, its grants, its SES events and its access log into one ordered list. */
export function buildTimeline(run: Run, sources: TimelineSources = {}): TimelineEvent[] {
  const events: TimelineEvent[] = [];

  const push = (
    id: string,
    at: Date | null,
    title: string,
    tone: TimelineTone,
    glyph: string,
    detail?: ReactNode,
  ): void => {
    if (!at) return;
    events.push({ id, at, sortKey: at.getTime(), title, tone, glyph, detail });
  };

  push(
    'run-started',
    toDate(run.started_at),
    'Run started',
    'info',
    '▸',
    <>
      {reportShapeInfo(run.report_shape).label} report
      {run.source_keys.length > 0 ? (
        <>
          {' '}· reading{' '}
          <span className="mono">
            {run.source_keys.length === 1
              ? run.source_keys[0]
              : `${run.source_keys.length} source objects`}
          </span>
        </>
      ) : null}
      {run.triggered_by ? <> · triggered by {run.triggered_by}</> : null}
    </>,
  );

  const grants = sources.grants ?? [];
  if (grants.length > 0) {
    const issued = grants
      .map((grant) => toDate(grant.issued_at))
      .filter((value): value is Date => value !== null)
      .sort((a, b) => a.getTime() - b.getTime());
    const expiries = Array.from(new Set(grants.map((grant) => grant.expires_at).filter(Boolean)));
    const firstIssued = issued[0] ?? null;
    // "links", not "download links": a `recording` grant is playback, and the grant table
    // below calls it that.
    const total = sources.grantTotal ?? grants.length;
    const truncated = Boolean(sources.grantsTruncated);
    push(
      'grants-issued',
      firstIssued,
      `${formatCount(total)} ${total === 1 ? 'link' : 'links'} issued`,
      'ok',
      '⊙',
      truncated ? (
        <>
          Expiries are not shown for all links — this page holds {formatCount(grants.length)} of{' '}
          {formatCount(total)}. See the links table below.
        </>
      ) : expiries.length === 1 ? (
        <>Expire {istDateTimeExact(expiries[0] as number)} — enforced by our grant, not by S3.</>
      ) : (
        <>Expiries differ across links — see the links table below.</>
      ),
    );

    for (const grant of grants) {
      if (!grant.revoked_at) continue;
      push(
        `grant-revoked-${grant.token_hash}`,
        toDate(grant.revoked_at),
        'Link revoked',
        'danger',
        '⊗',
        <>
          {grant.recipient_email ?? grant.recipient_id} · token{' '}
          <span className="mono">{maskToken(grant.token_hash)}</span> — the next click gets a 410.
        </>,
      );
    }
  }

  // --- SES -----------------------------------------------------------------
  // Repeated informational events collapse; every failure keeps its own line, with its
  // diagnostic in full — the reason an address bounced is the tail of that string.
  const sesEvents = sources.sesEvents ?? [];
  const collapsible = new Map<string, SesEvent[]>();

  for (const event of sesEvents) {
    if (SES_COLLAPSIBLE[event.event_type]) {
      const bucket = collapsible.get(event.event_type) ?? [];
      bucket.push(event);
      collapsible.set(event.event_type, bucket);
      continue;
    }
    const spec = SES_SPEC[event.event_type] ?? {
      title: event.event_type,
      tone: 'default' as TimelineTone,
      glyph: '·',
    };
    push(
      `ses-${event.message_id}-${event.event_key ?? event.event_at}`,
      toDate(event.event_at),
      spec.title,
      spec.tone,
      spec.glyph,
      <>
        {event.recipient}
        {event.detail ? <div className="small">{event.detail}</div> : null}
      </>,
    );
  }

  for (const [type, rows] of collapsible) {
    const spec = SES_SPEC[type] ?? { title: type, tone: 'default' as TimelineTone, glyph: '·' };
    const times = rows
      .map((row) => toDate(row.event_at))
      .filter((value): value is Date => value !== null)
      .sort((a, b) => a.getTime() - b.getTime());
    if (times.length === 0) continue;
    const addresses = Array.from(new Set(rows.map((row) => row.recipient).filter(Boolean)));

    if (rows.length === 1) {
      const only = rows[0] as SesEvent;
      push(
        `ses-${only.message_id}-${only.event_key ?? only.event_at}`,
        times[0] as Date,
        spec.title,
        spec.tone,
        spec.glyph,
        <>
          {only.recipient}
          {only.detail ? <div className="small">{only.detail}</div> : null}
        </>,
      );
      continue;
    }

    push(
      `ses-group-${type}`,
      times[0] as Date,
      spec.title,
      spec.tone,
      spec.glyph,
      <>
        {formatCount(rows.length)} {SES_COLLAPSIBLE[type] ?? 'events'} ·{' '}
        {pluralise(addresses.length, 'address', 'addresses')} · {spanLabel(times)} — see Delivery
        events below.
        {addresses.length > 0 ? (
          <div className="stack-tight" style={{ marginTop: 'var(--s-1)' }}>
            {addresses.map((address) => (
              <span key={address} className="mono xs nowrap">
                {address}
              </span>
            ))}
          </div>
        ) : null}
      </>,
    );
  }

  // --- Access log -----------------------------------------------------------
  // A successful download repeats; an expired or revoked click is a state worth its own
  // line, with the full user agent (it is how you tell a scanner from a person).
  const access = sources.access ?? [];
  const downloads = access.filter((row) => row.outcome === 'ok');
  const refusals = access.filter((row) => row.outcome !== 'ok');

  for (const row of refusals) {
    const spec =
      row.outcome === 'expired'
        ? { title: 'Expired link clicked', tone: 'warn' as TimelineTone, glyph: '◷' }
        : { title: 'Revoked link clicked', tone: 'danger' as TimelineTone, glyph: '⊗' };
    push(
      `access-${row.token_hash}-${row.accessed_at}`,
      toDate(row.accessed_at),
      spec.title,
      spec.tone,
      spec.glyph,
      <>
        token <span className="mono">{maskToken(row.token_hash)}</span> · {row.ip}
        {row.user_agent ? <div className="small dim">{row.user_agent}</div> : null}
      </>,
    );
  }

  if (downloads.length === 1) {
    const only = downloads[0] as AccessRow;
    push(
      `access-${only.token_hash}-${only.accessed_at}`,
      toDate(only.accessed_at),
      'Report downloaded',
      'ok',
      '↓',
      <>
        token <span className="mono">{maskToken(only.token_hash)}</span> · {only.ip}
        {only.user_agent ? <div className="small dim">{only.user_agent}</div> : null}
      </>,
    );
  } else if (downloads.length > 1) {
    const times = downloads
      .map((row) => toDate(row.accessed_at))
      .filter((value): value is Date => value !== null)
      .sort((a, b) => a.getTime() - b.getTime());
    const tokens = new Set(downloads.map((row) => row.token_hash));
    if (times.length > 0) {
      push(
        'access-downloads',
        times[0] as Date,
        'Report downloaded',
        'ok',
        '↓',
        <>
          {formatCount(downloads.length)} downloads through{' '}
          {pluralise(tokens.size, 'link', 'links')} · {spanLabel(times)} — see the Access log
          below.
        </>,
      );
    }
  }

  if (run.status === 'failed') {
    push(
      'run-failed',
      toDate(run.finished_at ?? null),
      'Run failed',
      'danger',
      '✕',
      run.error ? <>{run.error}</> : <>Nothing was delivered.</>,
    );
  } else if (run.status === 'stale') {
    push(
      'run-stale',
      toDate(run.finished_at ?? null),
      'Stopped — source was stale',
      'warn',
      '◈',
      run.error ?? 'The source object was missing or older than the window. Nothing was sent.',
    );
  } else if (run.status === 'sent') {
    const coverage = coverageOf(run);
    push(
      'run-finished',
      toDate(run.finished_at ?? null),
      // A run that finished holding a part day did not "finish" in the sense the reader
      // takes from a green tick, so the title says what it actually is.
      coverage.isPartial ? 'Run finished — part day' : 'Run finished',
      coverage.isPartial ? 'warn' : 'ok',
      coverage.isPartial ? '◧' : '■',
      <>
        {run.rows_out.toLocaleString('en-IN')} rows emitted to{' '}
        {run.recipients.length === 1 ? '1 recipient' : `${run.recipients.length} recipients`}.
        {coverage.isPartial ? <> {coverage.summary}</> : null}
        {coverage.rowsMisaligned !== null && coverage.rowsMisaligned > 0 ? (
          <> {misalignedNote(coverage)}</>
        ) : null}
      </>,
    );
  }

  return events
    .map((event, index) => ({ event, index }))
    .sort((a, b) => a.event.sortKey - b.event.sortKey || a.index - b.index)
    .map((entry) => entry.event);
}

// ---------------------------------------------------------------------------
// Phase rail
// ---------------------------------------------------------------------------

type PhaseState = 'done' | 'current' | 'pending' | 'failed' | 'stopped';

interface Phase {
  key: string;
  label: string;
  state: PhaseState;
  note: string;
}

const PHASE_SPEC: Record<
  PhaseState,
  { glyph: string; tone: TimelineTone; dashed: boolean; terminalBad?: boolean }
> = {
  done: { glyph: '✓', tone: 'ok', dashed: false },
  current: { glyph: '◑', tone: 'info', dashed: true },
  pending: { glyph: '·', tone: 'muted', dashed: false },
  failed: { glyph: '✕', tone: 'danger', dashed: false, terminalBad: true },
  stopped: { glyph: '◈', tone: 'warn', dashed: false },
};

export function buildPhases(run: Run, sources: TimelineSources = {}): Phase[] {
  const sesEvents = sources.sesEvents ?? [];
  const access = sources.access ?? [];
  const failed = run.status === 'failed';
  const stale = run.status === 'stale';

  const resolved: PhaseState =
    run.source_keys.length > 0
      ? stale
        ? 'stopped'
        : 'done'
      : run.status === 'resolving'
        ? 'current'
        : failed
          ? 'failed'
          : stale
            ? 'stopped'
            : 'pending';

  const generated: PhaseState =
    run.artifacts.length > 0
      ? 'done'
      : run.status === 'generating'
        ? 'current'
        : failed
          ? 'failed'
          : stale
            ? 'stopped'
            : 'pending';

  const sent: PhaseState =
    run.ses_message_ids.length > 0 || run.status === 'sent'
      ? 'done'
      : run.status === 'sending'
        ? 'current'
        : failed
          ? 'failed'
          : stale
            ? 'stopped'
            : 'pending';

  const bounced = sesEvents.some(
    (event) => event.event_type === 'Bounce' || event.event_type === 'Complaint' || event.event_type === 'Reject',
  );
  const deliveredCount = sesEvents.filter((event) => event.event_type === 'Delivery').length;
  const delivered: PhaseState = bounced
    ? 'failed'
    : deliveredCount > 0
      ? 'done'
      : sent === 'done'
        ? 'current'
        : 'pending';

  const downloads = access.filter((row) => row.outcome === 'ok').length;
  const downloaded: PhaseState = downloads > 0 ? 'done' : delivered === 'done' ? 'current' : 'pending';

  return [
    {
      key: 'resolved',
      label: 'Resolved',
      state: resolved,
      note:
        run.source_keys.length > 0
          ? `${run.source_keys.length} source ${run.source_keys.length === 1 ? 'object' : 'objects'}`
          : 'no source key yet',
    },
    {
      key: 'generated',
      label: 'Generated',
      state: generated,
      note:
        run.artifacts.length > 0
          ? `${run.rows_out.toLocaleString('en-IN')} rows`
          : 'no artifact yet',
    },
    {
      key: 'sent',
      label: 'Sent',
      state: sent,
      note:
        run.ses_message_ids.length > 0
          ? `${run.ses_message_ids.length} SES ${run.ses_message_ids.length === 1 ? 'message' : 'messages'}`
          : 'not handed to SES',
    },
    {
      key: 'delivered',
      label: bounced ? 'Bounced' : 'Delivered',
      state: delivered,
      note: bounced
        ? 'at least one address failed'
        : deliveredCount > 0
          ? `${deliveredCount} confirmed`
          : 'awaiting SES confirmation',
    },
    {
      key: 'downloaded',
      label: 'Downloaded',
      state: downloaded,
      note: downloads > 0 ? `${downloads} ${downloads === 1 ? 'download' : 'downloads'}` : 'not yet opened',
    },
  ];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface RunTimelineProps {
  run: Run;
  grants?: Grant[];
  /** The API's grant total, so the audit trail never states a page size as a fact. */
  grantTotal?: number | null;
  grantsTruncated?: boolean;
  access?: AccessRow[];
  sesEvents?: SesEvent[];
  loading?: boolean;
  /** Collapse past this many events, newest kept. */
  maxEvents?: number;
}

export function RunTimeline({
  run,
  grants,
  grantTotal,
  grantsTruncated,
  access,
  sesEvents,
  loading = false,
  maxEvents = 40,
}: RunTimelineProps) {
  const [expanded, setExpanded] = useState(false);
  const sources: TimelineSources = { grants, access, sesEvents, grantTotal, grantsTruncated };
  const phases = buildPhases(run, sources);
  const events = buildTimeline(run, sources);
  const hidden = Math.max(0, events.length - maxEvents);
  const shown = expanded || hidden === 0 ? events : events.slice(hidden);

  return (
    <div className="stack">
      <ol
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 'var(--s-3)',
          listStyle: 'none',
          margin: 0,
          padding: 0,
        }}
      >
        {phases.map((phase) => {
          const spec = PHASE_SPEC[phase.state];
          return (
            <li key={phase.key} className="cluster-tight" style={{ minWidth: 0 }}>
              <Badge
                tone={BADGE_TONE[spec.tone]}
                glyph={spec.glyph}
                pending={spec.dashed}
                terminalBad={spec.terminalBad}
              >
                {phase.label}
              </Badge>
              <span className="xs dim">{phase.note}</span>
            </li>
          );
        })}
      </ol>

      {hidden > 0 && !expanded ? (
        <div>
          <Button size="sm" variant="ghost" onClick={() => setExpanded(true)}>
            Show {hidden} earlier {hidden === 1 ? 'event' : 'events'}
          </Button>
        </div>
      ) : null}

      {loading && events.length === 0 ? (
        <div className="cluster">
          <span className="spinner" aria-hidden="true" />
          <span className="muted small">Loading delivery and access history…</span>
        </div>
      ) : null}

      {events.length === 0 && !loading ? (
        <div className="small dim">
          Nothing recorded yet beyond the run itself. Delivery and download events appear here as
          SES and the link resolver report them.
        </div>
      ) : (
        <ol style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {shown.map((event, index) => {
            const last = index === shown.length - 1;
            return (
              <li
                key={event.id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '24px minmax(0, 1fr)',
                  columnGap: 'var(--s-3)',
                }}
              >
                <div
                  style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minHeight: '100%' }}
                >
                  {/* The marker is a Badge carrying the glyph as its label: hue, glyph and
                      border come from the one place that defines them. */}
                  <Badge tone={BADGE_TONE[event.tone]} glyph={null} className="mono">
                    {event.glyph}
                  </Badge>
                  {last ? null : (
                    <span
                      aria-hidden="true"
                      style={{ flex: 1, width: 1, background: 'var(--line)', marginTop: 2 }}
                    />
                  )}
                </div>

                <div
                  className="stack-tight"
                  style={{ paddingBottom: last ? 0 : 'var(--s-3)', minWidth: 0 }}
                >
                  {/* Time on the title line, right-aligned and dim: one line per event
                      instead of two, and the eye reads down a column of times. The
                      absolute IST value stays on screen (CONTRACT §8.7); the relative one
                      is the supplement, on the tooltip. */}
                  <div className="cluster" style={{ gap: 'var(--s-2)' }}>
                    <span className="strong">{event.title}</span>
                    <span className="grow" />
                    <span className="xs dim nowrap num" title={timeAgo(event.at)}>
                      {istDateTimeExact(event.at)}
                    </span>
                  </div>
                  {event.detail ? <div className="small">{event.detail}</div> : null}
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

export default RunTimeline;
